import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { UserPlus, Building2, Briefcase } from 'lucide-react';
import api from '../services/api';

const ClientFormModal = ({
    isOpen, onClose, onSuccess,
    initialPhone = '', initialName = '',
    initialLid = '', // 🆕 Recebe LID separado
    initialCompany = '', initialEmail = '',
    initialNotes = '', initialIgnoreMetrics = false,
    initialUsePreferredAgent = false, initialPreferredAgentId = '', // 🆕
    clientId = null
}) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        lid: '', // 🆕 Adiciona campo LID
        company: '',
        email: '',
        notes: '',
        ignoreMetrics: false,
        usePreferredAgent: false, // 🆕
        preferredAgentId: '' // 🆕
    });

    // ... (rest of states are same)
    const [isNewCompanyModalOpen, setIsNewCompanyModalOpen] = useState(false);
    const [newCompanyName, setNewCompanyName] = useState('');
    const [creatingCompany, setCreatingCompany] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [allServices, setAllServices] = useState([]);
    const [users, setUsers] = useState([]); // 🆕 Lista de Atendentes
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadCompanies();
            loadUsers();
            api.get('/services').then(r => setAllServices(r.data || [])).catch(() => {});

            // Lógica de Separação LID/Telefone
            let phoneVal = initialPhone || '';
            let lidVal = initialLid || '';

            // Se o telefone contém @lid ou :, provavelmente é um LID que foi salvo no campo errado
            if (phoneVal.includes('@lid') || phoneVal.includes(':')) {
                if (!lidVal) lidVal = phoneVal; // Move para LID se estiver vazio
                phoneVal = ''; // Limpa telefone para o usuário corrigir
            }

            setFormData({
                name: initialName || '',
                phone: phoneVal,
                lid: lidVal,
                company: initialCompany || '',
                email: initialEmail || '',
                notes: initialNotes || '',
                ignoreMetrics: initialIgnoreMetrics || false,
                usePreferredAgent: initialUsePreferredAgent || false, // 🆕
                preferredAgentId: initialPreferredAgentId || '' // 🆕
            });
            setError('');
        }
    }, [isOpen, initialPhone, initialName, initialLid, initialCompany, initialEmail, initialNotes, initialIgnoreMetrics, initialUsePreferredAgent, initialPreferredAgentId]);

    const loadCompanies = async () => {
        try {
            const response = await api.get('/external-companies?limit=10000');
            // A API retorna paginado { data: [], total: ... } inside axios response.data
            // Se a API mudou para retornar { data: [...] }, precisamos acessar .data.data ou verificar
            // O backend retorna: res.json({ data, total, ... })
            // Axios response.data é o corpo JSON.
            // Então response.data.data é o array de empresas.
            setCompanies(response.data?.data || []);
        } catch (err) {
            console.error('Erro ao carregar empresas:', err);
            // Silencioso para não bloquear o fluxo
        }
    };

    const loadUsers = async () => {
        try {
            const response = await api.get('/users');
            setUsers(response.data || []);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
        }
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        if (!newCompanyName.trim()) return;

        setCreatingCompany(true);
        try {
            const res = await api.post('/external-companies', { name: newCompanyName });
            const newCompany = res.data;

            // Atualiza lista e seleciona a nova empresa
            setCompanies(prev => [...prev, newCompany]);
            handleChange('company', newCompany.name); // Salva o NOME

            setIsNewCompanyModalOpen(false);
            setNewCompanyName('');
        } catch (err) {
            alert('Erro ao criar empresa');
        } finally {
            setCreatingCompany(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validação
        if (!formData.name.trim()) {
            setError('Nome é obrigatório');
            return;
        }
        if (!formData.phone.trim()) {
            setError('Telefone é obrigatório');
            return;
        }
        if (!formData.company) {
            setError('Empresa é obrigatória');
            return;
        }

        setLoading(true);
        setError('');

        try {
            if (clientId) {
                // Atualização de cliente existente
                await api.put(`/clients/${clientId}`, formData);
            } else {
                // Criação de novo cliente
                await api.post('/clients', formData);
            }

            onSuccess && onSuccess();
            onClose();
        } catch (err) {
            console.error('Erro ao salvar cliente:', err);
            setError(err.response?.data?.error || 'Erro ao salvar cliente');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(''); // Limpa erro ao digitar
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={clientId ? "Atualizar Cliente" : "Cadastrar Cliente"}
                confirmText={loading ? "Salvando..." : "Salvar"}
                onConfirm={handleSubmit}
            >
                <form id="client-form" onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Nome */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">
                                Nome <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="Nome completo"
                                required
                                disabled={loading}
                            />
                        </div>

                        {/* Telefone */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">
                                Telefone <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="5521999999999"
                                required
                                disabled={loading}
                            />
                        </div>

                        {/* LID (Opcional/Read-only) */}
                        {formData.lid && (
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 mb-1">
                                    ID Interno (WhatsApp/LID) - <span className="text-gray-400 font-normal">Não altere se não souber o que está fazendo</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.lid}
                                    onChange={(e) => handleChange('lid', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-md text-xs font-mono"
                                    placeholder="LID..."
                                />
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Empresa */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">
                                <Building2 className="w-3 h-3 inline mr-1" />
                                Empresa <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        list="companies-list"
                                        value={formData.company}
                                        onChange={(e) => handleChange('company', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="Ex: ACME Ltda"
                                        required
                                        disabled={loading}
                                        autoComplete="off"
                                    />
                                    <datalist id="companies-list">
                                        {companies.map(c => <option key={c._id} value={c.name} />)}
                                    </datalist>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsNewCompanyModalOpen(true)}
                                    className="px-3 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 border border-blue-200 transition-colors flex-shrink-0 flex items-center justify-center"
                                    title="Cadastrar Nova Empresa"
                                >
                                    <span className="font-bold text-lg leading-none">+</span>
                                </button>
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">
                                Email (Opcional)
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="email@exemplo.com"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {/* 🆕 Bloco de Atendente Preferencial */}
                    <div className="border border-blue-100 bg-blue-50/50 p-3 rounded-lg space-y-3">
                        <div className="flex items-center">
                            <input
                                id="usePreferredAgent"
                                type="checkbox"
                                checked={formData.usePreferredAgent}
                                onChange={(e) => {
                                    handleChange('usePreferredAgent', e.target.checked);
                                    if (!e.target.checked) handleChange('preferredAgentId', '');
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                disabled={loading}
                            />
                            <label htmlFor="usePreferredAgent" className="ml-2 block text-sm font-bold text-blue-800">
                                ⭐ Usar Atendimento Preferencial?
                            </label>
                        </div>

                        {formData.usePreferredAgent && (
                            <div className="pl-6 animate-fade-in-down">
                                <label className="block text-xs font-bold text-gray-700 mb-1">
                                    Selecione o Atendente Responsável <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.preferredAgentId}
                                    onChange={(e) => handleChange('preferredAgentId', e.target.value)}
                                    className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    required={formData.usePreferredAgent}
                                    disabled={loading}
                                >
                                    <option value="">Selecione o atendente...</option>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Observações */}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                            Observações
                        </label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="Infos adicionais"
                            rows={2}
                            disabled={loading}
                        />
                    </div>

                    {/* Checkbox: Ignorar Métricas */}
                    <div className="flex items-center">
                        <input
                            id="ignoreMetrics"
                            type="checkbox"
                            checked={formData.ignoreMetrics}
                            onChange={(e) => handleChange('ignoreMetrics', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={loading}
                        />
                        <label htmlFor="ignoreMetrics" className="ml-2 block text-sm text-gray-900">
                            Ignorar Métricas deste Cliente (Não contabilizar em relatórios)
                        </label>
                    </div>

                    {/* Erro */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Campos obrigatórios */}
                    <p className="text-xs text-gray-500">
                        <span className="text-red-500">*</span> Campos obrigatórios
                    </p>
                </form>
            </Modal>

            {/* Modal de Nova Empresa */}
            <Modal
                isOpen={isNewCompanyModalOpen}
                onClose={() => setIsNewCompanyModalOpen(false)}
                title="Cadastrar Nova Empresa"
                confirmText={creatingCompany ? "Salvando..." : "Cadastrar"}
                onConfirm={handleCreateCompany}
                size="max-w-md"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome da Empresa
                        </label>
                        <input
                            type="text"
                            value={newCompanyName}
                            onChange={(e) => setNewCompanyName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ex: ACME Ltda"
                            autoFocus
                        />
                    </div>
                </div>

                    {/* 🆕 Serviços Contratados da Empresa Selecionada */}
                    {(() => {
                        // Resolve o objeto ExternalCompany que bate com o nome digitado
                        const selectedCompany = companies.find(
                            c => c.name?.toLowerCase().trim() === formData.company?.toLowerCase().trim()
                        );
                        if (!selectedCompany || !selectedCompany.services || selectedCompany.services.length === 0) return null;

                        const companyServices = selectedCompany.services
                            .map(sid => allServices.find(s => String(s._id || s.id) === String(sid._id || sid)))
                            .filter(Boolean);

                        if (companyServices.length === 0) return null;

                        return (
                            <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
                                <p className="text-xs font-bold text-blue-800 uppercase tracking-wide flex items-center gap-1.5">
                                    <Briefcase className="w-3.5 h-3.5" />
                                    Serviços Contratados por <span className="text-blue-600 font-semibold normal-case">{selectedCompany.name}</span>
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {companyServices.map(svc => (
                                        <span
                                            key={svc._id || svc.id}
                                            className="inline-flex items-center gap-1 bg-white border border-blue-300 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm"
                                        >
                                            <Briefcase className="w-3 h-3 text-blue-500" />
                                            {svc.name}
                                            {svc.price ? (
                                                <span className="ml-1 text-green-700 font-bold">
                                                    R$ {Number(svc.price).toFixed(2).replace('.', ',')}
                                                </span>
                                            ) : null}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
            </Modal>
        </>
    );
};

export default ClientFormModal;
